import { useState } from "react";

const API_BASE = "https://satu-analyzer.onrender.com/analyze";

function formatPrice(price) {
  return new Intl.NumberFormat("ru-RU").format(Math.round(price)) + " ₸";
}

function ScoreBadge({ score }) {
  const color =
    score >= 70
      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
      : score >= 50
        ? "bg-amber-50 text-amber-700 border border-amber-200"
        : "bg-gray-100 text-gray-500 border border-gray-200";
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${color}`}>
      {score}
    </span>
  );
}

// MUI-style outlined field wrapper
function OutlinedField({ label, children }) {
  return (
    <div className="relative border border-gray-300 rounded px-3 pt-3 pb-2 bg-white hover:border-gray-400 transition-colors">
      <span className="absolute -top-2 left-2 bg-white px-1 text-[11px] text-gray-500 leading-none">
        {label}
      </span>
      {children}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="flex flex-col gap-0.5 bg-white rounded border border-gray-200 px-4 py-3">
      <span className="text-[11px] text-gray-400 uppercase tracking-wider font-medium">
        {label}
      </span>
      <span className={`text-lg font-bold ${accent || "text-gray-800"}`}>
        {value}
      </span>
    </div>
  );
}

// Spinner matching the screenshot
function Spinner() {
  return (
    <div className="flex items-center gap-3 py-8 px-2">
      <svg
        className="animate-spin"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="3" />
        <path
          d="M12 2a10 10 0 0 1 10 10"
          stroke="#3b82f6"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

export default function SatuAnalyzer() {
  const [query, setQuery] = useState("");
  const [endPage, setEndPage] = useState(1);
  const [strictTitle, setStrictTitle] = useState(false);
  const [bestLimit, setBestLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setData(null);
    const params = new URLSearchParams({
      query: query.trim(),
      selected_source: "satu",
      selected_category: "auto",
      start_page: "1",
      end_page: String(endPage),
      remove_outliers: "false",
      strict_title_match: String(strictTitle),
      best_limit: String(bestLimit),
    });
    try {
      const res = await fetch(`${API_BASE}?${params}`);
      if (!res.ok) throw new Error(`Ошибка ${res.status}: ${res.statusText}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuery("");
    setEndPage(1);
    setStrictTitle(false);
    setBestLimit(20);
    setData(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800">
      <div className="max-w-6xl mx-auto px-4 py-5 space-y-4">
        {/* ── Filter bar ── */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3 shadow-sm">
          {/* Row 1: all controls */}
          <div className="flex flex-wrap items-end gap-3">
            {/* Search – widest */}
            <div className="flex-1 min-w-[180px]">
              <OutlinedField label="Поиск">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="ноутбук lenovo..."
                  className="w-full text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent"
                />
              </OutlinedField>
            </div>

            {/* Fixed: source */}
            <div className="w-28">
              <OutlinedField label="Источник">
                <div className="flex items-center gap-1">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="flex-shrink-0"
                  >
                    <rect width="11" height="11" x="11" y="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                  </svg>
                  <span className="text-sm text-gray-400 font-mono">satu</span>
                </div>
              </OutlinedField>
            </div>

            {/* Fixed: category */}
            <div className="w-28">
              <OutlinedField label="Категория">
                <div className="flex items-center gap-1">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="flex-shrink-0"
                  >
                    <rect width="11" height="11" x="11" y="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                  </svg>
                  <span className="text-sm text-gray-400 font-mono">auto</span>
                </div>
              </OutlinedField>
            </div>

            {/* Fixed: start_page */}
            <div className="w-32">
              <OutlinedField label="Нач. страница">
                <div className="flex items-center gap-1">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="flex-shrink-0"
                  >
                    <rect width="11" height="11" x="11" y="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                  </svg>
                  <span className="text-sm text-gray-400 font-mono">1</span>
                </div>
              </OutlinedField>
            </div>

            {/* Fixed: remove_outliers */}
            <div className="w-32">
              <OutlinedField label="Убр. выбросы">
                <div className="flex items-center gap-1">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="flex-shrink-0"
                  >
                    <rect width="11" height="11" x="11" y="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                  </svg>
                  <span className="text-sm text-gray-400 font-mono">false</span>
                </div>
              </OutlinedField>
            </div>

            {/* Editable: end_page */}
            <div className="w-36">
              <OutlinedField label="Кон. страница">
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={endPage}
                  onChange={(e) => setEndPage(Number(e.target.value))}
                  className="w-full text-sm text-gray-800 outline-none bg-transparent font-mono"
                />
              </OutlinedField>
            </div>

            {/* Editable: best_limit */}
            <div className="w-36">
              <OutlinedField label="Лимит офферов">
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={bestLimit}
                  onChange={(e) => setBestLimit(Number(e.target.value))}
                  className="w-full text-sm text-gray-800 outline-none bg-transparent font-mono"
                />
              </OutlinedField>
            </div>

            {/* Editable: strict_title_match checkbox */}
            <label className="flex items-center gap-2 cursor-pointer select-none pb-2">
              <div
                onClick={() => setStrictTitle((v) => !v)}
                className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  strictTitle
                    ? "bg-blue-600 border-blue-600"
                    : "border-gray-400 bg-white"
                }`}
              >
                {strictTitle && (
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M2 6l3 3 5-5"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <span className="text-sm text-gray-600 leading-tight">
                Строгое
                <br />
                совпадение
              </span>
            </label>
          </div>

          {/* Row 2: action buttons */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleSearch}
              disabled={loading || !query.trim()}
              className="px-5 py-2 rounded text-white text-sm font-bold uppercase tracking-wide bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Применить
            </button>
            <button
              onClick={handleClear}
              className="px-5 py-2 rounded text-white text-sm font-bold uppercase tracking-wide bg-red-500 hover:bg-red-600 transition-colors"
            >
              Очистить
            </button>
          </div>
        </div>

        {/* ── Error ── */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded px-4 py-3 text-sm text-red-600 flex items-center gap-2">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            {error}
          </div>
        )}

        {/* ── Loading ── */}
        {loading && <Spinner />}

        {/* ── No results ── */}
        {data && !loading && data.total_found === 0 && (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white py-12 text-center">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#d1d5db"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect width="20" height="14" x="2" y="5" rx="2" />
              <path d="M2 10h20" />
            </svg>
            <p className="text-sm font-semibold text-gray-600">
              Совпадений не найдено
            </p>
            <p className="text-xs text-gray-400">
              {data.message ||
                "По данному запросу подходящих товаров на Satu.kz не найдено"}
            </p>
          </div>
        )}

        {/* ── Results ── */}
        {data && !loading && data.total_found > 0 && (
          <div className="space-y-4">
            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="Всего найдено" value={data.total_found} />
              <StatCard
                label="Мин. цена"
                value={formatPrice(data.price_stats?.min_price)}
                accent="text-emerald-600"
              />
              <StatCard
                label="Средняя цена"
                value={formatPrice(data.price_stats?.average_price)}
              />
              <StatCard
                label="Макс. цена"
                value={formatPrice(data.price_stats?.max_price)}
                accent="text-red-500"
              />
            </div>

            {/* Price range bar */}
            {data.price_stats?.normal_range && (
              <div className="bg-white border border-gray-200 rounded-lg px-5 py-4 shadow-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Нормальный диапазон цен
                  </span>
                  <span className="text-xs text-gray-400">
                    Медиана:{" "}
                    <span className="font-semibold text-gray-600">
                      {formatPrice(data.price_stats.median_price)}
                    </span>
                  </span>
                </div>
                <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                  {(() => {
                    const min = data.price_stats.min_price;
                    const max = data.price_stats.max_price;
                    const range = max - min || 1;
                    const fromPct =
                      ((data.price_stats.normal_range.from - min) / range) *
                      100;
                    const toPct =
                      ((data.price_stats.normal_range.to - min) / range) * 100;
                    return (
                      <div
                        className="absolute top-0 h-full bg-blue-500 rounded-full"
                        style={{
                          left: `${fromPct}%`,
                          width: `${Math.max(toPct - fromPct, 0)}%`,
                        }}
                      />
                    );
                  })()}
                </div>
                <div className="flex justify-between mt-1 text-[11px] text-gray-400">
                  <span>{formatPrice(data.price_stats.normal_range.from)}</span>
                  <span>{formatPrice(data.price_stats.normal_range.to)}</span>
                </div>
              </div>
            )}

            {/* Suppliers table */}
            {data.suppliers?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Поставщики · {data.suppliers.length}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left px-5 py-2.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          Поставщик
                        </th>
                        <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          Товаров
                        </th>
                        <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          Мин.
                        </th>
                        <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          Средняя
                        </th>
                        <th className="text-right px-5 py-2.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          Макс.
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {data.suppliers.map((s, i) => (
                        <tr
                          key={i}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-5 py-3 font-medium text-gray-700">
                            {s.name}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="bg-blue-50 text-blue-600 text-xs font-semibold px-2 py-0.5 rounded">
                              {s.products_count}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-600 font-medium">
                            {formatPrice(s.min_price)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600">
                            {formatPrice(s.average_price)}
                          </td>
                          <td className="px-5 py-3 text-right text-gray-400">
                            {formatPrice(s.max_price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Best offers */}
            {data.best_offers?.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-0.5">
                  Лучшие предложения · {data.best_offers.length}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.best_offers.map((offer, i) => (
                    <a
                      key={i}
                      href={offer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all flex gap-3 p-3"
                    >
                      {/* Thumb */}
                      <div className="w-[72px] h-[72px] flex-shrink-0 rounded border border-gray-100 bg-gray-50 overflow-hidden">
                        {offer.image_url ? (
                          <img
                            src={offer.image_url}
                            alt={offer.title}
                            className="w-full h-full object-contain p-1"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-200">
                            <svg
                              width="22"
                              height="22"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect width="18" height="18" x="3" y="3" rx="2" />
                              <circle cx="9" cy="9" r="2" />
                              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0 flex flex-col gap-1">
                        <p className="text-sm text-gray-700 font-medium line-clamp-2 leading-snug group-hover:text-blue-600 transition-colors">
                          {offer.title}
                        </p>
                        <div className="flex items-center gap-2 mt-auto">
                          <span className="text-base font-bold text-gray-800">
                            {formatPrice(offer.price)}
                          </span>
                          <ScoreBadge score={offer.score} />
                        </div>
                        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-gray-400">
                          <span>{offer.supplier}</span>
                          {offer.company_region && (
                            <>
                              <span>·</span>
                              <span>{offer.company_region}</span>
                            </>
                          )}
                          {offer.available && (
                            <>
                              <span>·</span>
                              <span className="text-emerald-500 font-semibold">
                                В наличии
                              </span>
                            </>
                          )}
                        </div>
                        {offer.reason && (
                          <p className="text-[11px] text-gray-400 italic leading-tight">
                            {offer.reason}
                          </p>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {data.removed_as_outliers > 0 && (
              <p className="text-center text-xs text-gray-400">
                Убрано как выбросы:{" "}
                <span className="font-semibold">
                  {data.removed_as_outliers}
                </span>{" "}
                из {data.total_before_outlier_filter}
              </p>
            )}
          </div>
        )}

        {/* ── Empty state ── */}
        {!data && !loading && !error && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#d1d5db"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mb-3"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <p className="text-gray-400 text-sm">
              Введите запрос и нажмите «Применить»
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
